import React, { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import SmartInventoryPanel from "@/components/SmartInventoryPanel";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, UploadSimple, DownloadSimple, FileCsv, FileXls } from "@phosphor-icons/react";
import {
  MaterialActions, MaterialEditDialog, MaterialDuplicateDialog,
  MaterialDeleteDialog, MaterialAdjustDialog
} from "@/components/MaterialCrudDialogs";
import { useAuth } from "@/context/AuthContext";

export default function Materials() {
  const { user: currentUser } = useAuth();
  const [mats, setMats] = useState([]);
  const [sups, setSups] = useState([]);
  const [open, setOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editMat, setEditMat] = useState(null);
  const [viewMat, setViewMat] = useState(null);
  const [dupMat, setDupMat] = useState(null);
  const [delMat, setDelMat] = useState(null);
  const [adjMat, setAdjMat] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ code: "", name: "", category: "Vegetables", unit: "kg", stock: 0, min_stock: 0, rate: 0, supplier_id: "" });

  const canImport = ["admin", "purchase", "store"].includes(currentUser?.role);

  const load = async () => {
    setMats((await api.get("/materials", { params: { include_inactive: showInactive }})).data);
    setSups((await api.get("/suppliers")).data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showInactive]);

  const create = async () => {
    try {
      const sup = sups.find(s => s.id === form.supplier_id);
      await api.post("/materials", { ...form, supplier_name: sup?.name });
      toast.success("Material added");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const doExport = async (ext) => {
    const token = localStorage.getItem("chericious_token");
    try {
      const res = await fetch(`${API}/materials/export.${ext}?include_inactive=${showInactive}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `raw_materials.${ext}`;
      a.click();
      toast.success(`Exported raw_materials.${ext}`);
    } catch (e) { toast.error(`Export failed: ${e.message}`); }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("chericious_token");
    const res = await fetch(`${API}/materials/import/template.xlsx`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "materials_import_template.xlsx";
    a.click();
  };

  const doImport = async () => {
    if (!importFile) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const token = localStorage.getItem("chericious_token");
      const res = await fetch(`${API}/materials/import-file`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `${res.status}`);
      setImportResult(data);
      toast.success(`Import complete: ${data.created} created, ${data.updated} updated${data.errors.length ? `, ${data.errors.length} errors` : ""}`);
      load();
    } catch (e) { toast.error(`Import failed: ${e.message}`); }
    finally { setImporting(false); }
  };

  return (
    <div data-testid="materials-page">
      <PageHeader subtitle="Inventory" title="Raw Material Inventory"
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap"
                   data-testid="mat-show-inactive-label">
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
                     data-testid="mat-show-inactive"/>
              Show inactive
            </label>
            <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} data-testid="mat-export-xlsx">
              <DownloadSimple size={14} className="mr-1.5"/>Export
            </Button>
            {canImport && (
              <Button variant="outline" size="sm" onClick={() => { setImportFile(null); setImportResult(null); setImportOpen(true); }} data-testid="mat-import-btn">
                <UploadSimple size={14} className="mr-1.5"/>Import
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-material-btn"><Plus size={16} className="mr-1.5"/>Add Material</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Raw Material</DialogTitle><DialogDescription className="sr-only">Add Raw Material</DialogDescription></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code</Label><Input value={form.code} onChange={e=>setForm({...form, code:e.target.value})}/></div>
                <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={v=>setForm({...form, category:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["Vegetables","Pulses","Rice","Spices","Oil","Ghee","Milk Products","Packaging","Labels","Cartons"].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Unit</Label>
                  <Select value={form.unit} onValueChange={v=>setForm({...form, unit:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{["kg","g","L","pc"].map(u=><SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={e=>setForm({...form, stock:parseFloat(e.target.value)})}/></div>
                <div><Label>Min Stock</Label><Input type="number" value={form.min_stock} onChange={e=>setForm({...form, min_stock:parseFloat(e.target.value)})}/></div>
                <div><Label>Rate (₹)</Label><Input type="number" value={form.rate} onChange={e=>setForm({...form, rate:parseFloat(e.target.value)})}/></div>
                <div><Label>Supplier</Label>
                  <Select value={form.supplier_id} onValueChange={v=>setForm({...form, supplier_id:v})}>
                    <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                    <SelectContent>{sups.map(s=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="material-save">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="mb-6">
        <SmartInventoryPanel/>
      </div>

      <Card className="shadow-none">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Stock Level</TableHead>
                <TableHead className="text-right">Rate ₹</TableHead>
                <TableHead className="text-right">Value ₹</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mats.map(m => {
                const low = m.stock <= m.min_stock;
                const pct = Math.min(100, (m.stock / Math.max(m.min_stock * 2, 1)) * 100);
                return (
                  <TableRow key={m.id} className={m.status && m.status !== "active" ? "opacity-70" : ""} data-testid={`material-row-${m.code}`}>
                    <TableCell className="mono font-semibold">
                      {m.code}
                      {m.status && m.status !== "active" && (
                        <Badge variant="secondary" className="ml-1.5 uppercase text-[9px]" data-testid={`mat-status-${m.code}`}>{m.status}</Badge>
                      )}
                    </TableCell>
                    <TableCell>{m.name}</TableCell>
                    <TableCell><Badge variant="secondary">{m.category}</Badge></TableCell>
                    <TableCell className="min-w-[200px]">
                      <div className="flex justify-between text-[11px] mb-1">
                        <span className="mono">{m.stock} {m.unit}</span>
                        <span className="text-muted-foreground">min {m.min_stock}</span>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                    </TableCell>
                    <TableCell className="text-right mono">{m.rate}</TableCell>
                    <TableCell className="text-right mono">{(m.stock * m.rate).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{m.supplier_name || "—"}</TableCell>
                    <TableCell>{low ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Low</Badge> : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">OK</Badge>}</TableCell>
                    <TableCell>
                      <MaterialActions
                        mat={m}
                        currentUser={currentUser}
                        onEdit={setEditMat}
                        onView={setViewMat}
                        onDuplicate={setDupMat}
                        onDelete={setDelMat}
                        onAdjust={setAdjMat}
                        onRestore={async (mat) => {
                          try { await api.post(`/materials/${mat.id}/restore`); toast.success("Material restored successfully."); load(); }
                          catch (e) { toast.error(e.response?.data?.detail || e.message); }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <MaterialEditDialog open={!!editMat} onOpenChange={(o) => !o && setEditMat(null)} material={editMat} mode="edit" onSaved={load}/>
      <MaterialEditDialog open={!!viewMat} onOpenChange={(o) => !o && setViewMat(null)} material={viewMat} mode="view"/>
      <MaterialDuplicateDialog open={!!dupMat} onOpenChange={(o) => !o && setDupMat(null)} material={dupMat} onDone={load}/>
      <MaterialDeleteDialog open={!!delMat} onOpenChange={(o) => !o && setDelMat(null)} material={delMat} onDone={load}/>
      <MaterialAdjustDialog open={!!adjMat} onOpenChange={(o) => !o && setAdjMat(null)} material={adjMat} onDone={load}/>

      {/* ---------- Import Dialog ---------- */}
      <Dialog open={importOpen} onOpenChange={o => { if (!o) { setImportOpen(false); setImportFile(null); setImportResult(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Raw Materials</DialogTitle>
            <DialogDescription className="text-xs">
              Upload an Excel (.xlsx) or CSV file. Existing materials are matched by <b>code</b> and updated in place; new codes are created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Not sure of the format? {" "}
              <button className="text-primary font-semibold underline" onClick={downloadTemplate} data-testid="mat-import-template">
                Download the template
              </button>
              {" "}with headers and one sample row.
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={e => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                className="text-sm"
                data-testid="mat-import-file"
              />
            </div>
            {importResult && (
              <div className="rounded border p-3 bg-muted/30 text-sm space-y-1">
                <div className="flex gap-4">
                  <span><b>{importResult.created}</b> created</span>
                  <span><b>{importResult.updated}</b> updated</span>
                  <span className={importResult.errors.length ? "text-red-600" : ""}><b>{importResult.errors.length}</b> errors</span>
                  <span className="text-muted-foreground ml-auto">of {importResult.total_rows} rows</span>
                </div>
                {importResult.errors.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5">
                    {importResult.errors.map((e, i) => (
                      <div key={i} className="text-xs text-red-700">
                        Row {e.row}{e.code ? ` (${e.code})` : ""}: {e.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
            <Button onClick={doImport} disabled={!importFile || importing} data-testid="mat-import-submit">
              {importing ? "Importing…" : "Upload & Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
